import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import {
  DesignServices,
  DriveFileRenameOutline,
  ArrowCircleDown,
  Visibility,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { DSRC_FILTER_API_TYPE, DSRCFilter } from '../DSRCFilter';
import { dorpgsOverViewFilter } from '../filters';
import { frcApplicationListFilter } from '../../../FRCAssessment';
import {
  type DRCNonProposedData,
  useGetNonProposed,
} from '../../../graphql/DRCAssessment';
import { MONITOR_LIST_TEXT } from '../../../lang';
import {
  type HeadCell,
  AdvancedTable,
  Alert,
  type AlertDetails,
  Button,
  ButtonContainer,
  DeepMerge,
  IconButton,
  Loading,
  pageRowsSelectOptions,
} from '../../../../../components';
import { useAuth } from '../../../../../contexts';
import {
  handleViewAsApplicant,
  APPLICATION_STATUS_TYPE,
  APPLICATION_STATUS_EXPLANATION_MAP,
  ASSESSMENT_STATUS_DISPLAY_MAP,
  type PostRequestData,
  postRequest,
  handlePageLoading,
  fundingCell,
} from '../../../../../utils';
import { OneOffDialog, type OneOffDialogConfig } from '../../../components';

export enum OverViewRole {
  FRC = 'FRC',
  DRC = 'DRC',
  GS = 'GS',
  INTERVIEW_PANEL = 'INTERVIEW_PANEL',
}

export type DRCorFRCOverViewListProps = {
  filter: any;
  overViewRole: OverViewRole;
  children?: React.ReactNode;
};

export const DRCorFRCOverViewList: React.FunctionComponent<
  DRCorFRCOverViewListProps
> = (props: DRCorFRCOverViewListProps) => {
  const { filter: initialFilter, overViewRole, children } = props;
  const { user } = useAuth();
  const [pageLoading, setPageLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });
  const [filter, setFilter] = useState(initialFilter);
  const [page, setPage] = useState(1);
  const [oneOffDialogConfig, setOpenOneOffDialog] =
    useState<OneOffDialogConfig>({
      open: false,
      applicationId: '',
      applicationNo: '',
    });

  const [selectRowsLimit, setSelectRowsLimit] = useState(
    pageRowsSelectOptions[0].value as number,
  );
  const [appliedFilter, setAppliedFilter] = useState<any>(null);

  // same display as the non proposed list
  // because both of them are using applicationViewsPaginated
  const { nonProposedApplications, loading, error } = useGetNonProposed(
    filter,
    {},
    { page, limit: selectRowsLimit },
  );
  const pageCount = nonProposedApplications?.totalPage ?? 1;
  const recordsCount = nonProposedApplications?.count ?? 0;
  const visibleData = nonProposedApplications?.data ?? [];

  const showDept = overViewRole !== OverViewRole.DRC;
  const overViewCells: Array<HeadCell<DRCNonProposedData>> = [
    {
      id: 'applicationNo',
      label: MONITOR_LIST_TEXT.APPLICATION_NO,
      isSortable: false,
      render: (row: DRCNonProposedData) => row.applicationNo,
    },
    {
      id: 'applicantName',
      label: MONITOR_LIST_TEXT.APPLICANT_NAME,
      isSortable: false,
      render: (row: DRCNonProposedData) => row.applicantName,
    },
    {
      id: 'progLevel',
      label: MONITOR_LIST_TEXT.PROG_LEVEL,
      isSortable: false,
      render: (row: DRCNonProposedData) => row.progLevel,
    },
    {
      id: 'entryYear',
      label: MONITOR_LIST_TEXT.ENTRY_YEAR,
      isSortable: false,
      render: (row: DRCNonProposedData) => row.entryYear,
    },
    {
      id: 'researchField',
      label: MONITOR_LIST_TEXT.RESEARCH_FIELD,
      isSortable: false,
      render: (row: DRCNonProposedData) => row.researchField,
    },
    {
      id: 'inWhichSupervisorList',
      label: MONITOR_LIST_TEXT.CHIEF_SUPERVISOR,
      isSortable: false,
      render: (row: DRCNonProposedData) => row.inWhichSupervisorList,
    },
    {
      id: 'studyMode',
      label: MONITOR_LIST_TEXT.STUDY_MODE,
      isSortable: false,
      render: (row: DRCNonProposedData) => row.studyMode,
    },
    {
      id: 'programmeType',
      label: MONITOR_LIST_TEXT.PROG_TYPE,
      isSortable: false,
      render: (row: DRCNonProposedData) => row.programmeType,
    },
    {
      id: 'fundingScheme',
      label: `${MONITOR_LIST_TEXT.FUNDING_SCHEME} | ${MONITOR_LIST_TEXT.STATUS}`,
      isSortable: false,
      minWidth: 25,
      render: (row: DRCNonProposedData) => {
        return fundingCell(row.fundingScheme ?? []);
      },
    },
    ...(showDept && !loading
      ? ([
          {
            id: 'department',
            label: MONITOR_LIST_TEXT.DEPARTMENT,
            isSortable: false,
            render: (row: DRCNonProposedData) => row?.department,
          },
        ] as Array<HeadCell<DRCNonProposedData>>)
      : []),
    {
      id: 'HKPFSReferenceNumber',
      label: MONITOR_LIST_TEXT.HKPFS_REFERENCE_NUMBER,
      isSortable: false,
      render: (row: DRCNonProposedData) => row.HKPFSReferenceNumber,
    },
    {
      id: 'validHKPFSApplication',
      label: MONITOR_LIST_TEXT.VALID_HKPFS_APPLICATION,
      isSortable: false,
      render: (row: DRCNonProposedData) => {
        // Heavy check mark for valid; Heavy multiplication for invalid; '-' for no verification
        const invalidSymbol =
          row.validHKPFSApplication === null ? '-' : '\u2716';
        const validHKPFSApplication = row.validHKPFSApplication
          ? '\u2714'
          : invalidSymbol;

        return validHKPFSApplication;
      },
    },

    {
      id: 'lastNominationSubmissionTime',
      label: MONITOR_LIST_TEXT.CS_SUBMISSION_TIME,
      isSortable: false,
      minWidth: 8,
      render: (row: DRCNonProposedData) => row.lastNominationSubmissionTime,
    },
    {
      id: 'status',
      label: MONITOR_LIST_TEXT.STATUS,
      isSortable: false,
      render: (row: DRCNonProposedData) => {
        const rowStatus = row.status as APPLICATION_STATUS_TYPE;
        const explanation = APPLICATION_STATUS_EXPLANATION_MAP[rowStatus];
        return (
          <Tooltip title={explanation}>
            <span>{ASSESSMENT_STATUS_DISPLAY_MAP[rowStatus]}</span>
          </Tooltip>
        );
      },
    },
    {
      id: 'action',
      label: MONITOR_LIST_TEXT.ACTION,
      isSortable: false,
      minWidth: 10,
      render: (row: DRCNonProposedData) => action(row),
    },
  ];

  const handleSwitchViewAssessment =
    (applicationId: string): (() => void) =>
    () => {
      const viewNav: Record<string, string> = {
        [OverViewRole.DRC]: '/internal/drc-homepage/drcassessment/details/',
        [OverViewRole.FRC]: '/internal/frc-homepage/frcassessment/details/',
        [OverViewRole.GS]: '/internal/gs-homepage/gsassessment/details/',
      };
      window.open(viewNav[overViewRole] + applicationId);
    };

  const action = (row: DRCNonProposedData): React.ReactNode => {
    const { status, applicationId, applicationNo } = row;

    const editable =
      (status === APPLICATION_STATUS_TYPE.UNDER_DSRC &&
        overViewRole === OverViewRole.DRC) ||
      (status === APPLICATION_STATUS_TYPE.UNDER_FRCSB &&
        overViewRole === OverViewRole.FRC) ||
      (status === APPLICATION_STATUS_TYPE.UNDER_GS &&
        overViewRole === OverViewRole.GS);

    return (
      <div className="flex justify-center items-center">
        <div className="grid grid-cols-2">
          <IconButton
            onClick={handleSwitchViewAssessment(applicationId)}
            disabled={!editable}
            color={editable ? 'primary' : 'inherit'}
            icon={<DriveFileRenameOutline />}
          />

          <IconButton
            onClick={handleViewMonitor(applicationId)}
            color="primary"
            icon={<Visibility />}
          />

          {overViewRole === OverViewRole.GS && (
            <IconButton
              onClick={handleViewAsApplicant(applicationId, status)}
              title="View as Applicant"
              color="primary"
              icon={<DesignServices />}
            />
          )}

          <IconButton
            onClick={() => {
              setOpenOneOffDialog({
                open: true,
                applicationId,
                applicationNo,
              });
            }}
            title="Download Profile"
            color="primary"
            icon={<ArrowCircleDown />}
          />
        </div>
      </div>
    );
  };

  const handleRequestSort = (property: keyof DRCNonProposedData): void => {
    // TODO: Get the sorted data from the server
  };

  const handleViewMonitor =
    (applicationId: string): (() => void) =>
    () => {
      if (applicationId) window.open(`/internal/monitor/${applicationId}`);
    };

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSelectRowsLimit(parseInt(event.target.value));
    setPage(1);
  };

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const handleExportGSApplication = async (): Promise<void> => {
    await handlePageLoading(setPageLoading, async () => {
      try {
        // pass filter json as post body
        const postData: PostRequestData = {
          url: `/eadmin-operation/general/export/applications`,
          data: filter,
          contentType: 'application/json',
        };
        const response = await postRequest(postData);

        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        const formattedDateTime = dayjs().format('DD/MM/YYYY_HH-mm-ss');
        link.setAttribute(
          'download',
          `GS-applications-data-${formattedDateTime}.csv`,
        );

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Revoke the URL to free up memory
        URL.revokeObjectURL(url);
      } catch (error) {
        setAlertDetails({
          alertContent: 'Error Occurs.' + (error as Error)?.message,
          alertType: 'error',
        });
        setOpenAlert(true);
      }
    });
  };

  const origFilterMappings: Record<string, any> = {
    [OverViewRole.DRC]: dorpgsOverViewFilter(user),
    // FRC should use the different filter
    // because they could view applications from several departments
    [OverViewRole.FRC]: frcApplicationListFilter(user),
    [OverViewRole.GS]: {},
  };

  const { open: openDialog } = oneOffDialogConfig;
  return (
    <div>
      <Loading openLoading={pageLoading} />
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {openDialog && (
        <OneOffDialog
          openDialogConfig={oneOffDialogConfig}
          onDialogClose={() => {
            setOpenOneOffDialog({
              open: false,
              applicationId: '',
              applicationNo: '',
            });
          }}
        />
      )}

      <DSRCFilter
        initialAppliedFilter={appliedFilter}
        setFilter={(filter: any, appliedFilter: any) => {
          setPage(1);
          setFilter(DeepMerge(filter, origFilterMappings[overViewRole]));
          setAppliedFilter(appliedFilter);
        }}
        apiType={DSRC_FILTER_API_TYPE.applicationsPaginated}
        overViewRole={overViewRole}
      />
      {overViewRole === OverViewRole.GS && (
        <ButtonContainer
          width={'100%'}
          display={'flex'}
          justifyContent={'end'}
          spacing={1}
        >
          <Button
            onClick={handleExportGSApplication}
            startIcon={<ArrowCircleDown />}
            variant="text"
            className="!normal-case"
          >
            {MONITOR_LIST_TEXT.EXPORT_ALL_DATA}
          </Button>
        </ButtonContainer>
      )}
      {loading ? (
        <Loading />
      ) : (
        <AdvancedTable<DRCNonProposedData>
          tableProps={{
            headCells: overViewCells,
            data: visibleData,
            handleRequestSort,
          }}
          extendFunctionAreas={
            <div className="w-full">
              <div className="flex justify-between w-full">{children}</div>
            </div>
          }
          advancedPaginationProps={{
            page,
            count: pageCount,
            recordText: `${recordsCount} records`,
            rowsPerPageOptions: pageRowsSelectOptions,
            rowsPerPage: selectRowsLimit,
            onChangeRowsPerPage: handleChangeRowsPerPage,
          }}
          handleChangePage={handleChangePage}
        />
      )}
    </div>
  );
};
